// The application's own checkout, shared by every surface that can run one:
// the platform's Run view and live preview, the published public site, and a
// downloaded runtime. They differ only in `apiBase` — the gateway dance, the
// verification round-trip and the error wording are the same everywhere, and
// a second copy of them would be a second place for a payment bug to live.
//
// WHAT THIS FILE DOES NOT DECIDE: the amount. It posts the record's identity
// and the button's binding; the server reads the record and resolves what to
// charge. Everything that comes back here is already settled server-side.

export interface PaymentPublicConfig {
  configured: boolean
  enabled: boolean
  provider: string
  mode?: string
  key_id?: string
  currency?: string
  methods?: string[]
  cod_enabled: boolean
  brand?: { name?: string; logo?: string; color?: string }
}

export interface CreatedCheckout {
  order_id: string
  provider: string
  mode?: string
  key_id: string
  provider_order_id: string
  amount_minor: number
  amount: number
  currency: string
  reference: string
  prefill?: { name?: string; email?: string; contact?: string }
  brand?: { name?: string; logo?: string; color?: string }
  methods?: string[]
  // Stripe only — scoped to this one PaymentIntent.
  client_secret?: string
}

export interface CheckoutOrder {
  id: string
  status: string
  amount: number
  currency: string
  reference: string
  written_back: boolean
  writeback_error?: string
}

export interface PaymentApi {
  // `${origin}/api/app-payments/${owner}/${project}` on the platform;
  // `/app/<name>/api/app-payments/<owner>/<project>` in a downloaded runtime.
  base: string
  // The surface's own fetch, so a site session's headers / a platform
  // cookie / a bearer token all keep working without this module knowing
  // which it is.
  fetch: (url: string, init?: RequestInit) => Promise<Response>
}

export class PaymentCancelled extends Error {
  constructor() {
    super('Payment was cancelled.')
    this.name = 'PaymentCancelled'
  }
}

/** The gateway refused the payment (and the customer closed its window).
 *  The ORDER is still placed — the caller offers to pay again. */
export class PaymentFailed extends Error {
  constructor(message?: string) {
    super(message || 'Your payment did not go through.')
    this.name = 'PaymentFailed'
  }
}

async function json<T>(api: PaymentApi, path: string, init?: RequestInit): Promise<T> {
  const res = await api.fetch(`${api.base}${path}`, {
    ...init,
    headers: { 'Content-Type': 'application/json', ...(init?.headers || {}) },
  })
  const text = await res.text()
  let body: unknown = null
  try { body = text ? JSON.parse(text) : null } catch { body = null }
  if (!res.ok) {
    const detail = (body as { detail?: unknown } | null)?.detail
    const message = typeof detail === 'string'
      ? detail
      : (detail as { message?: string } | undefined)?.message
        || `Payment request failed (${res.status})`
    throw new Error(message)
  }
  return body as T
}

export function fetchPaymentConfig(api: PaymentApi): Promise<PaymentPublicConfig> {
  return json<PaymentPublicConfig>(api, '/config')
}

export interface StartPaymentInput {
  objName: string
  txnId: string
  binding: Record<string, unknown>
  payer?: { name?: string; email?: string; contact?: string }
  // Shown in the gateway's own modal when the gateway supports a title.
  description?: string
}

/**
 * Create the order, open the gateway, and return once the payment is settled
 * server-side.
 *
 * The verify round-trip at the end is not decoration: the browser's word that
 * a payment succeeded is a claim, and the server checks it with the gateway
 * before anything is marked paid. If verification fails after the gateway
 * said yes — a genuine possibility when a webhook has not landed yet — the
 * caller is told the payment is being confirmed rather than that it failed,
 * because the money may well have moved.
 */
export async function startPayment(
  api: PaymentApi, input: StartPaymentInput,
): Promise<{ order: CheckoutOrder; pending: boolean }> {
  const created = await json<{ order: CheckoutOrder; checkout: CreatedCheckout }>(
    api, '/orders', {
      method: 'POST',
      body: JSON.stringify({
        obj_name: input.objName, txn_id: input.txnId,
        binding: input.binding, payer: input.payer || null,
      }),
    })
  const checkout = created.checkout
  let result: Record<string, unknown>
  try {
    result = checkout.provider === 'stripe'
      ? await runStripe(checkout)
      : await runRazorpay(checkout, input.description)
  } catch (err) {
    if (err instanceof PaymentFailed) {
      // Tell the server, which checks with the gateway before recording it —
      // so the order list can say "payment failed" and offer to pay again.
      // Best effort: the webhook reports the same failure.
      await json(api, `/orders/${encodeURIComponent(checkout.order_id)}/failed`,
        { method: 'POST', body: JSON.stringify({ reason: err.message }) }).catch(() => null)
    }
    throw err
  }

  try {
    const verified = await json<{ order: CheckoutOrder }>(
      api, `/orders/${encodeURIComponent(checkout.order_id)}/verify`,
      { method: 'POST', body: JSON.stringify({ payload: result }) })
    return { order: verified.order, pending: false }
  } catch (err) {
    // The gateway accepted it and we could not confirm. Do NOT call this a
    // failure — the webhook is the authoritative path and may already have
    // settled it. Hand back the order as it stands so the caller can poll.
    const current = await pollOrder(api, checkout.order_id).catch(() => null)
    if (current && current.status === 'paid') return { order: current, pending: false }
    if (current) return { order: current, pending: true }
    throw err
  }
}

export interface CashOnDeliveryInput {
  objName: string
  txnId: string
  // The same binding a payment would send. Only its COLUMN NAMES are used,
  // to find the model's payment action; the status stamped is that action's
  // `cod_status_value`, read by the server from the model.
  binding: Record<string, unknown>
}

export interface CashOnDeliveryResult {
  ok: boolean
  // True when the record already read the cash-on-delivery status.
  already: boolean
  status: string
  reference: string
  obj_name: string
  txn_id: string
}

/**
 * Record a cash-on-delivery choice on the customer's own order, through the
 * payment rail. The record's payment columns are writable ONLY by that rail
 * (the engine refuses anyone else), which is why this is its own endpoint
 * rather than a save of the record. A refusal — the project does not take
 * COD, the order is someone else's, it is already paid, or the application's
 * own rules say no — is thrown with the server's message.
 */
export function placeCashOnDelivery(
  api: PaymentApi, input: CashOnDeliveryInput,
): Promise<CashOnDeliveryResult> {
  return json<CashOnDeliveryResult>(api, '/cod', {
    method: 'POST',
    body: JSON.stringify({
      obj_name: input.objName, txn_id: input.txnId, binding: input.binding,
    }),
  })
}

export async function pollOrder(api: PaymentApi, orderId: string): Promise<CheckoutOrder> {
  const res = await json<{ order: CheckoutOrder }>(
    api, `/orders/${encodeURIComponent(orderId)}`)
  return res.order
}

/**
 * Wait for the webhook to settle an order the browser could not confirm.
 * Bounded on purpose: an order that has not settled in half a minute is not
 * going to settle while the customer watches, and telling them "we will email
 * you" beats a spinner that never stops.
 */
export async function awaitSettlement(
  api: PaymentApi, orderId: string, { attempts = 10, intervalMs = 3000 } = {},
): Promise<CheckoutOrder | null> {
  for (let i = 0; i < attempts; i++) {
    await new Promise(resolve => setTimeout(resolve, intervalMs))
    try {
      const order = await pollOrder(api, orderId)
      if (order.status !== 'created') return order
    } catch {
      // A transient read failure is not an answer; keep waiting.
    }
  }
  return null
}

// ── gateway SDKs ────────────────────────────────────────────────────────────

const SDK_URLS: Record<string, string> = {
  razorpay: 'https://checkout.razorpay.com/v1/checkout.js',
  stripe: 'https://js.stripe.com/v3/',
}

/** Load a gateway's script once per page and resolve when it is usable. */
function loadSdk(provider: string): Promise<void> {
  const url = SDK_URLS[provider]
  if (!url) return Promise.reject(new Error(`Unknown payment provider "${provider}".`))
  if (typeof document === 'undefined') {
    return Promise.reject(new Error('Payments need a browser.'))
  }
  const existing = document.querySelector<HTMLScriptElement>(`script[src="${url}"]`)
  if (existing && existing.dataset.loaded === 'true') return Promise.resolve()
  return new Promise((resolve, reject) => {
    const script = existing || document.createElement('script')
    script.src = url
    script.async = true
    script.addEventListener('load', () => { script.dataset.loaded = 'true'; resolve() })
    script.addEventListener('error', () => reject(
      new Error('The payment gateway could not be reached. Check your connection '
                + 'and try again.')))
    if (!existing) document.body.appendChild(script)
  })
}

function runRazorpay(checkout: CreatedCheckout, description?: string): Promise<Record<string, unknown>> {
  return loadSdk('razorpay').then(() => new Promise((resolve, reject) => {
    const Razorpay = (window as unknown as { Razorpay?: new (o: unknown) => { open: () => void } }).Razorpay
    if (!Razorpay) { reject(new Error('The payment gateway did not load.')); return }
    let settled = false
    // Razorpay keeps its window open after a declined attempt so the
    // customer can try another method there; remember the last refusal so
    // closing the window afterwards reads as a FAILED payment, not a cancel.
    let lastFailure = ''
    const rzp = new Razorpay({
      key: checkout.key_id,
      order_id: checkout.provider_order_id,
      amount: checkout.amount_minor,
      currency: checkout.currency,
      name: checkout.brand?.name || '',
      description: description || checkout.reference,
      image: checkout.brand?.logo || undefined,
      theme: checkout.brand?.color ? { color: checkout.brand.color } : undefined,
      prefill: {
        name: checkout.prefill?.name || '',
        email: checkout.prefill?.email || '',
        contact: checkout.prefill?.contact || '',
      },
      handler: (response: Record<string, unknown>) => { settled = true; resolve(response) },
      modal: {
        // Closing the modal is a cancellation, not an error — the order stays
        // `created` and the customer can try again with the same record.
        ondismiss: () => {
          if (settled) return
          reject(lastFailure ? new PaymentFailed(lastFailure) : new PaymentCancelled())
        },
      },
    }) as { open: () => void; on?: (ev: string, cb: (r: { error?: { description?: string; reason?: string } }) => void) => void }
    rzp.on?.('payment.failed', (r) => {
      lastFailure = r?.error?.description || 'Your payment was declined.'
    })
    rzp.open()
  }))
}

interface StripeLike {
  confirmPayment: (args: unknown) => Promise<{ error?: { message?: string };
    paymentIntent?: { id: string; status: string } }>
  elements: (args: unknown) => unknown
}

function runStripe(checkout: CreatedCheckout): Promise<Record<string, unknown>> {
  return loadSdk('stripe').then(() => {
    const factory = (window as unknown as { Stripe?: (k: string) => StripeLike }).Stripe
    if (!factory) throw new Error('The payment gateway did not load.')
    if (!checkout.client_secret) {
      throw new Error('This Stripe order is missing its client secret.')
    }
    // Stripe has no hosted modal equivalent to Razorpay's; the surface mounts
    // a Payment Element and calls back here. Until a surface does that, a
    // redirect confirm is the honest fallback: it works everywhere and never
    // silently no-ops.
    const stripe = factory(checkout.key_id)
    return stripe.confirmPayment({
      clientSecret: checkout.client_secret,
      confirmParams: { return_url: window.location.href },
      redirect: 'if_required',
    }).then(result => {
      if (result.error) throw new Error(result.error.message || 'The payment was declined.')
      const intent = result.paymentIntent
      if (!intent) throw new PaymentCancelled()
      return { payment_intent: intent.id, status: intent.status } as Record<string, unknown>
    })
  })
}
